import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Stack,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ACCOUNTING_API } from '../../../../config/api';
import { accountingApi } from '../../../../config/apiClient';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface AddLoanModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preselectedBankAccountId?: number;
}

interface DeductionRow {
  name: string;
  amount: string;
}

interface InstallmentRow {
  dueDate: string; // YYYY-MM-DD
  amount: string;
  principalPart: string;
  interestPart: string;
}

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const toFinalDate = (dateStr: string): string => {
  const selected = new Date(dateStr);
  const now = new Date();
  const isToday =
    selected.getFullYear() === now.getFullYear() &&
    selected.getMonth() === now.getMonth() &&
    selected.getDate() === now.getDate();
  const finalDate = isToday
    ? new Date()
    : new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 12, 0, 0);
  return finalDate.toISOString();
};

/**
 * Türk para formatı: nokta = binlik ayracı, virgül = ondalık.
 * "200.000,50" → 200000.50
 */
const parseAmount = (v: string): number => {
  const cleaned = (v || '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

/**
 * Tutar girişini yazarken canlı biçimlendirir: "200000" → "200.000", "200000,5" → "200.000,5".
 * Yalnızca rakam ve tek virgül kabul eder; ondalık en fazla 2 hane.
 */
const formatAmountInput = (raw: string): string => {
  // Rakam ve virgül dışındakileri at (kullanıcı nokta yazarsa binlik ayracı olarak yok sayılır)
  let cleaned = raw.replace(/[^\d,]/g, '');
  const firstComma = cleaned.indexOf(',');
  let intPart = firstComma >= 0 ? cleaned.slice(0, firstComma) : cleaned;
  let decPart = firstComma >= 0 ? cleaned.slice(firstComma + 1).replace(/,/g, '') : null;
  intPart = intPart.replace(/^0+(?=\d)/, ''); // baştaki gereksiz sıfırlar
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  if (decPart === null) return grouped;
  return `${grouped},${decPart.slice(0, 2)}`;
};

/** Sayıyı input'ta gösterilecek Türk formatlı string'e çevirir (22916.67 → "22.916,67") */
const numToInput = (n: number): string =>
  n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Ay ekleme — ay sonu taşmalarını korur (31 Oca + 1 ay = 28/29 Şub) */
const addMonthsClamped = (base: Date, months: number): Date => {
  const y = base.getFullYear();
  const m = base.getMonth() + months;
  const lastDay = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(base.getDate(), lastDay));
};

const AddLoanModal: React.FC<AddLoanModalProps> = ({ open, onClose, onSuccess, preselectedBankAccountId }) => {
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kaynak listeleri
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [cashboxes, setCashboxes] = useState<any[]>([]);

  // Adım 1 — Kredi bilgileri + kesintiler
  const [bankAccountId, setBankAccountId] = useState<string>('');
  const [name, setName] = useState('');
  const [loanNumber, setLoanNumber] = useState('');
  const [loanDate, setLoanDate] = useState(todayStr());
  const [principal, setPrincipal] = useState('');
  const [totalPayback, setTotalPayback] = useState('');
  const [notes, setNotes] = useState('');
  const [deductions, setDeductions] = useState<DeductionRow[]>([]);

  // Adım 2 — Taksit planı
  const [installmentCount, setInstallmentCount] = useState('12');
  const [firstDueDate, setFirstDueDate] = useState('');
  const [period, setPeriod] = useState<'monthly' | 'weekly' | 'quarterly'>('monthly');
  const [installments, setInstallments] = useState<InstallmentRow[]>([]);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Adım 3 — Aktarım
  const [disbursementTarget, setDisbursementTarget] = useState<'NONE' | 'BANK' | 'CASHBOX'>('NONE');
  const [disbursementBankId, setDisbursementBankId] = useState<string>('');
  const [disbursementCashboxId, setDisbursementCashboxId] = useState<string>('');

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setError(null);
      setBankAccountId(preselectedBankAccountId ? String(preselectedBankAccountId) : '');
      setName('');
      setLoanNumber('');
      setLoanDate(todayStr());
      setPrincipal('');
      setTotalPayback('');
      setNotes('');
      setDeductions([]);
      setInstallmentCount('12');
      // İlk vade varsayılanı: bir ay sonrası
      setFirstDueDate(fmtDate(addMonthsClamped(new Date(), 1)));
      setPeriod('monthly');
      setInstallments([]);
      setShowBreakdown(false);
      setDisbursementTarget('NONE');
      setDisbursementBankId('');
      setDisbursementCashboxId('');
      loadSources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselectedBankAccountId]);

  const loadSources = async () => {
    try {
      const [cbRes, bankRes] = await Promise.all([
        accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY),
        accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY),
      ]);
      setCashboxes(cbRes.data?.data || cbRes.data || []);
      setBankAccounts(bankRes.data?.data || bankRes.data || []);
    } catch (err) {
      console.error('[AddLoanModal] Kaynaklar yüklenemedi:', err);
    }
  };

  // ── Türetilmiş değerler ─────────────────────────────────
  const selectedBank = useMemo(
    () => bankAccounts.find((b: any) => String(b.id) === bankAccountId),
    [bankAccounts, bankAccountId]
  );
  const currency = selectedBank?.currency || 'TRY';

  const principalVal = parseAmount(principal);
  const paybackVal = parseAmount(totalPayback);
  const totalInterest = paybackVal - principalVal;
  const deductionTotal = deductions.reduce((s, d) => s + parseAmount(d.amount), 0);
  const netDisbursed = principalVal - deductionTotal;

  const installmentSum = installments.reduce((s, i) => s + parseAmount(i.amount), 0);
  const tolerance = 0.01 * Math.max(installments.length, 1);
  const sumMatches = installments.length > 0 && Math.abs(installmentSum - paybackVal) <= tolerance;
  const datesAscending = useMemo(() => {
    for (let k = 1; k < installments.length; k++) {
      if (!installments[k].dueDate || !installments[k - 1].dueDate) return false;
      if (installments[k].dueDate <= installments[k - 1].dueDate) return false;
    }
    return installments.every((i) => !!i.dueDate);
  }, [installments]);

  const fmt = (n: number) => n.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Adım geçerlilik kontrolleri ─────────────────────────
  const step1Valid =
    !!bankAccountId &&
    name.trim().length > 0 &&
    principalVal > 0 &&
    paybackVal >= principalVal &&
    deductions.every((d) => d.name.trim().length > 0 && parseAmount(d.amount) >= 0) &&
    deductionTotal < principalVal;

  const step2Valid =
    installments.length >= 1 &&
    installments.every((i) => parseAmount(i.amount) > 0) &&
    datesAscending &&
    sumMatches;

  const step3Valid =
    disbursementTarget === 'NONE' ||
    (disbursementTarget === 'BANK' && !!disbursementBankId) ||
    (disbursementTarget === 'CASHBOX' && !!disbursementCashboxId);

  // ── Kesinti işlemleri ───────────────────────────────────
  const quickDeductions = [
    tt('Dosya Masrafı', 'File Fee'),
    tt('Hayat Sigortası', 'Life Insurance'),
    'BSMV',
    'KKDF',
    tt('Damga Vergisi', 'Stamp Duty'),
    tt('Ekspertiz', 'Appraisal'),
  ];

  const addDeduction = (presetName?: string) => {
    setDeductions((prev) => [...prev, { name: presetName || '', amount: '' }]);
  };

  const updateDeduction = (idx: number, field: keyof DeductionRow, value: string) => {
    setDeductions((prev) => prev.map((d, i) => (i === idx ? { ...d, [field]: value } : d)));
  };

  const removeDeduction = (idx: number) => {
    setDeductions((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Taksit planı üretimi ────────────────────────────────
  const generatePlan = () => {
    const n = parseInt(installmentCount, 10);
    if (!n || n < 1 || n > 360) {
      setError(tt('Geçerli bir taksit sayısı giriniz (1-360).', 'Enter a valid installment count (1-360).'));
      return;
    }
    if (!firstDueDate) {
      setError(tt('İlk vade tarihini seçiniz.', 'Select the first due date.'));
      return;
    }
    if (paybackVal <= 0) {
      setError(tt('Önce toplam geri ödeme tutarını giriniz.', 'Enter the total payback amount first.'));
      return;
    }
    setError(null);

    // Eşit taksit: kuruş farkı son taksite eklenir
    const base = Math.floor((paybackVal / n) * 100) / 100;
    const last = Math.round((paybackVal - base * (n - 1)) * 100) / 100;

    const first = new Date(firstDueDate);
    const rows: InstallmentRow[] = [];
    for (let i = 0; i < n; i++) {
      let due: Date;
      if (period === 'weekly') {
        due = new Date(first);
        due.setDate(due.getDate() + i * 7);
      } else if (period === 'quarterly') {
        due = addMonthsClamped(first, i * 3);
      } else {
        due = addMonthsClamped(first, i);
      }
      rows.push({
        dueDate: fmtDate(due),
        amount: numToInput(i === n - 1 ? last : base),
        principalPart: '',
        interestPart: '',
      });
    }
    setInstallments(rows);
  };

  const updateInstallment = (idx: number, field: keyof InstallmentRow, value: string) => {
    setInstallments((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const removeInstallment = (idx: number) => {
    setInstallments((prev) => prev.filter((_, i) => i !== idx));
  };

  const addInstallmentRow = () => {
    const lastRow = installments[installments.length - 1];
    const nextDate = lastRow?.dueDate
      ? fmtDate(addMonthsClamped(new Date(lastRow.dueDate), 1))
      : firstDueDate || todayStr();
    setInstallments((prev) => [...prev, { dueDate: nextDate, amount: '', principalPart: '', interestPart: '' }]);
  };

  // ── Kaydet ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!step1Valid || !step2Valid || !step3Valid) return;
    try {
      setLoading(true);
      setError(null);

      // ⚠️ Kural: request body PascalCase; CompanyId body'ye KONMAZ (JWT/X-Company-Id)
      const payload = {
        BankAccountId: parseInt(bankAccountId, 10),
        Name: name.trim(),
        LoanNumber: loanNumber.trim() || null,
        PrincipalAmount: principalVal,
        TotalPayback: paybackVal,
        LoanDate: toFinalDate(loanDate),
        Notes: notes.trim() || null,
        Deductions: deductions
          .filter((d) => parseAmount(d.amount) > 0)
          .map((d) => ({ Name: d.name.trim(), Amount: parseAmount(d.amount) })),
        Installments: installments.map((r, i) => ({
          InstallmentNo: i + 1,
          DueDate: toFinalDate(r.dueDate),
          Amount: parseAmount(r.amount),
          PrincipalPart: parseAmount(r.principalPart) > 0 ? parseAmount(r.principalPart) : null,
          InterestPart: parseAmount(r.interestPart) > 0 ? parseAmount(r.interestPart) : null,
        })),
        DisbursementTarget: disbursementTarget,
        DisbursementBankAccountId: disbursementTarget === 'BANK' ? parseInt(disbursementBankId, 10) : null,
        DisbursementCashboxId: disbursementTarget === 'CASHBOX' ? parseInt(disbursementCashboxId, 10) : null,
        CreatedBy: userDetails?.email || 'Sistem',
      };

      await accountingApi.post(ACCOUNTING_API.BANK_LOAN_CREATE, payload);
      setLoading(false);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || err.message || tt('Kredi oluşturulamadı', 'Could not create loan'));
    }
  };

  const steps = [
    tt('Kredi Bilgileri', 'Loan Details'),
    tt('Taksit Planı', 'Installment Plan'),
    tt('Aktarım & Özet', 'Transfer & Summary'),
  ];

  // Para birimine göre filtrelenmiş aktarım kaynakları
  const filteredBanks = bankAccounts.filter((b: any) => (b.currency || 'TRY') === currency);
  const filteredCashboxes = cashboxes.filter((c: any) => (c.currency || 'TRY') === currency);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{tt('Kredi Ekle', 'Add Loan')}</Typography>
          <IconButton edge="end" onClick={onClose} disabled={loading}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ═══════════ ADIM 1 — Kredi Bilgileri + Kesintiler ═══════════ */}
        {activeStep === 0 && (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="loan-bank-label">{tt('Kredinin Çekildiği Banka *', 'Loan Bank Account *')}</InputLabel>
                <Select
                  labelId="loan-bank-label"
                  value={bankAccountId}
                  label={tt('Kredinin Çekildiği Banka *', 'Loan Bank Account *')}
                  onChange={(e) => setBankAccountId(String(e.target.value))}
                  disabled={loading}
                >
                  {bankAccounts.map((b: any) => (
                    <MenuItem key={b.id} value={String(b.id)}>
                      {b.bankName} — {b.accountNumber} ({b.currency})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={tt('Kredi Adı *', 'Loan Name *')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                disabled={loading}
              />
              <TextField
                label={tt('Kredi/Sözleşme No', 'Loan/Contract No')}
                value={loanNumber}
                onChange={(e) => setLoanNumber(e.target.value)}
                fullWidth
                disabled={loading}
              />
              <TextField
                label={tt('Kullanım Tarihi *', 'Disbursement Date *')}
                type="date"
                value={loanDate}
                onChange={(e) => setLoanDate(e.target.value)}
                fullWidth
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={tt('Anapara *', 'Principal *')}
                value={principal}
                onChange={(e) => setPrincipal(formatAmountInput(e.target.value))}
                fullWidth
                disabled={loading}
                placeholder="200.000"
                InputProps={{ endAdornment: <Typography variant="body2" color="textSecondary">{currency}</Typography> }}
              />
              <TextField
                label={tt('Toplam Geri Ödeme *', 'Total Payback *')}
                value={totalPayback}
                onChange={(e) => setTotalPayback(formatAmountInput(e.target.value))}
                fullWidth
                disabled={loading}
                placeholder="275.000"
                InputProps={{ endAdornment: <Typography variant="body2" color="textSecondary">{currency}</Typography> }}
              />
            </Box>

            {principalVal > 0 && paybackVal > 0 && (
              <Alert severity={totalInterest >= 0 ? 'info' : 'error'} sx={{ mb: 2 }}>
                {totalInterest >= 0
                  ? `${tt('Toplam Faiz', 'Total Interest')}: ${fmt(totalInterest)} ${currency}`
                  : tt('Toplam geri ödeme anaparadan küçük olamaz!', 'Total payback cannot be less than principal!')}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{tt('Kesintiler', 'Deductions')}</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              {quickDeductions.map((q) => (
                <Chip key={q} label={`+ ${q}`} size="small" onClick={() => addDeduction(q)} disabled={loading} />
              ))}
              <Chip
                label={tt('+ Kesinti Ekle', '+ Add Deduction')}
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => addDeduction()}
                disabled={loading}
              />
            </Stack>

            {deductions.map((d, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  label={tt('Kesinti Adı', 'Deduction Name')}
                  value={d.name}
                  onChange={(e) => updateDeduction(idx, 'name', e.target.value)}
                  size="small"
                  sx={{ flex: 2 }}
                  disabled={loading}
                />
                <TextField
                  label={tt('Tutar', 'Amount')}
                  value={d.amount}
                  onChange={(e) => updateDeduction(idx, 'amount', formatAmountInput(e.target.value))}
                  size="small"
                  sx={{ flex: 1 }}
                  disabled={loading}
                />
                <IconButton size="small" color="error" onClick={() => removeDeduction(idx)} disabled={loading}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Paper
              variant="outlined"
              sx={{ p: 2, mt: 2, bgcolor: netDisbursed >= 0 ? 'success.50' : 'error.50', borderColor: netDisbursed >= 0 ? 'success.main' : 'error.main' }}
            >
              <Typography variant="body2" color="textSecondary">
                {tt('Kesinti Toplamı', 'Total Deductions')}: {fmt(deductionTotal)} {currency}
              </Typography>
              <Typography variant="h6" color={netDisbursed >= 0 ? 'success.main' : 'error.main'}>
                {tt('Hesaba Geçecek Net Tutar', 'Net Amount to Account')}: {fmt(netDisbursed)} {currency}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* ═══════════ ADIM 2 — Taksit Planı ═══════════ */}
        {activeStep === 1 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label={tt('Taksit Sayısı', 'Installment Count')}
                type="number"
                value={installmentCount}
                onChange={(e) => setInstallmentCount(e.target.value)}
                size="small"
                sx={{ width: 130 }}
                disabled={loading}
              />
              <TextField
                label={tt('İlk Vade Tarihi', 'First Due Date')}
                type="date"
                value={firstDueDate}
                onChange={(e) => setFirstDueDate(e.target.value)}
                size="small"
                sx={{ width: 180 }}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl size="small" sx={{ width: 140 }}>
                <InputLabel id="loan-period-label">{tt('Periyot', 'Period')}</InputLabel>
                <Select
                  labelId="loan-period-label"
                  value={period}
                  label={tt('Periyot', 'Period')}
                  onChange={(e) => setPeriod(e.target.value as any)}
                  disabled={loading}
                >
                  <MenuItem value="monthly">{tt('Aylık', 'Monthly')}</MenuItem>
                  <MenuItem value="weekly">{tt('Haftalık', 'Weekly')}</MenuItem>
                  <MenuItem value="quarterly">{tt('3 Aylık', 'Quarterly')}</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" onClick={generatePlan} disabled={loading}>
                {tt('Planı Oluştur', 'Generate Plan')}
              </Button>
              <Button size="small" onClick={() => setShowBreakdown((v) => !v)} disabled={loading}>
                {showBreakdown ? tt('Kırılımı Gizle', 'Hide Breakdown') : tt('Anapara/Faiz Kırılımı', 'Principal/Interest Breakdown')}
              </Button>
            </Box>

            {installments.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}>No</TableCell>
                      <TableCell>{tt('Vade', 'Due Date')}</TableCell>
                      <TableCell>{tt('Tutar', 'Amount')}</TableCell>
                      {showBreakdown && <TableCell>{tt('Anapara', 'Principal')}</TableCell>}
                      {showBreakdown && <TableCell>{tt('Faiz', 'Interest')}</TableCell>}
                      <TableCell width={50} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installments.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            value={r.dueDate}
                            onChange={(e) => updateInstallment(idx, 'dueDate', e.target.value)}
                            size="small"
                            variant="standard"
                            disabled={loading}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={r.amount}
                            onChange={(e) => updateInstallment(idx, 'amount', formatAmountInput(e.target.value))}
                            size="small"
                            variant="standard"
                            sx={{ width: 110 }}
                            disabled={loading}
                          />
                        </TableCell>
                        {showBreakdown && (
                          <TableCell>
                            <TextField
                              value={r.principalPart}
                              onChange={(e) => updateInstallment(idx, 'principalPart', formatAmountInput(e.target.value))}
                              size="small"
                              variant="standard"
                              sx={{ width: 100 }}
                              disabled={loading}
                              placeholder="-"
                            />
                          </TableCell>
                        )}
                        {showBreakdown && (
                          <TableCell>
                            <TextField
                              value={r.interestPart}
                              onChange={(e) => updateInstallment(idx, 'interestPart', formatAmountInput(e.target.value))}
                              size="small"
                              variant="standard"
                              sx={{ width: 100 }}
                              disabled={loading}
                              placeholder="-"
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeInstallment(idx)} disabled={loading}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={addInstallmentRow} disabled={loading}>
                {tt('Taksit Ekle', 'Add Installment')}
              </Button>
              {installments.length > 0 && (
                <Alert severity={sumMatches && datesAscending ? 'success' : 'error'} sx={{ py: 0 }}>
                  {tt('Taksit Toplamı', 'Installments Total')}: {fmt(installmentSum)} / {tt('Hedef', 'Target')}: {fmt(paybackVal)} {currency}
                  {!sumMatches && ` (${tt('Fark', 'Diff')}: ${fmt(installmentSum - paybackVal)})`}
                  {!datesAscending && ` — ${tt('Vade tarihleri artan sıralı olmalı!', 'Due dates must be ascending!')}`}
                </Alert>
              )}
            </Box>
          </Box>
        )}

        {/* ═══════════ ADIM 3 — Aktarım + Özet ═══════════ */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {tt('Net Tutar Aktarımı', 'Net Amount Transfer')} ({fmt(netDisbursed)} {currency})
            </Typography>
            <RadioGroup
              value={disbursementTarget}
              onChange={(e) => setDisbursementTarget(e.target.value as any)}
              sx={{ mb: 2 }}
            >
              <FormControlLabel value="NONE" control={<Radio />} label={tt('Aktarma (para hareketi oluşmaz)', 'Do not transfer (no money movement)')} disabled={loading} />
              <FormControlLabel value="BANK" control={<Radio />} label={tt('Banka hesabına aktar', 'Transfer to bank account')} disabled={loading} />
              <FormControlLabel value="CASHBOX" control={<Radio />} label={tt('Kasaya aktar', 'Transfer to cashbox')} disabled={loading} />
            </RadioGroup>

            {disbursementTarget === 'BANK' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="disb-bank-label">{tt('Banka Hesabı Seçin *', 'Select Bank Account *')}</InputLabel>
                <Select
                  labelId="disb-bank-label"
                  value={disbursementBankId}
                  label={tt('Banka Hesabı Seçin *', 'Select Bank Account *')}
                  onChange={(e) => setDisbursementBankId(String(e.target.value))}
                  disabled={loading || filteredBanks.length === 0}
                >
                  {filteredBanks.length === 0 ? (
                    <MenuItem disabled value="">{tt('Uygun banka hesabı bulunamadı', 'No suitable bank account found')}</MenuItem>
                  ) : filteredBanks.map((b: any) => (
                    <MenuItem key={b.id} value={String(b.id)}>
                      {b.bankName} — {b.accountNumber} ({tt('Bakiye', 'Balance')}: {Number(b.balance || 0).toLocaleString(localeCode)} {b.currency})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {disbursementTarget === 'CASHBOX' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="disb-cashbox-label">{tt('Kasa Seçin *', 'Select Cashbox *')}</InputLabel>
                <Select
                  labelId="disb-cashbox-label"
                  value={disbursementCashboxId}
                  label={tt('Kasa Seçin *', 'Select Cashbox *')}
                  onChange={(e) => setDisbursementCashboxId(String(e.target.value))}
                  disabled={loading || filteredCashboxes.length === 0}
                >
                  {filteredCashboxes.length === 0 ? (
                    <MenuItem disabled value="">{tt('Uygun kasa bulunamadı', 'No suitable cashbox found')}</MenuItem>
                  ) : filteredCashboxes.map((c: any) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name} ({tt('Bakiye', 'Balance')}: {Number(c.balance || 0).toLocaleString(localeCode)} {c.currency})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label={tt('Notlar', 'Notes')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
              disabled={loading}
            />

            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{tt('Özet', 'Summary')}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                <Typography variant="body2" color="textSecondary">{tt('Kredi Adı', 'Loan Name')}:</Typography>
                <Typography variant="body2">{name || '-'}</Typography>
                <Typography variant="body2" color="textSecondary">{tt('Banka', 'Bank')}:</Typography>
                <Typography variant="body2">{selectedBank ? `${selectedBank.bankName} — ${selectedBank.accountNumber}` : '-'}</Typography>
                <Typography variant="body2" color="textSecondary">{tt('Anapara', 'Principal')}:</Typography>
                <Typography variant="body2">{fmt(principalVal)} {currency}</Typography>
                <Typography variant="body2" color="textSecondary">{tt('Toplam Geri Ödeme', 'Total Payback')}:</Typography>
                <Typography variant="body2">{fmt(paybackVal)} {currency}</Typography>
                <Typography variant="body2" color="textSecondary">{tt('Toplam Faiz', 'Total Interest')}:</Typography>
                <Typography variant="body2" color="warning.main">{fmt(totalInterest)} {currency}</Typography>
              </Box>
              {deductions.filter((d) => parseAmount(d.amount) > 0).length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>{tt('Kesintiler', 'Deductions')}:</Typography>
                  {deductions.filter((d) => parseAmount(d.amount) > 0).map((d, i) => (
                    <Typography key={i} variant="body2" sx={{ pl: 1 }}>
                      • {d.name}: {fmt(parseAmount(d.amount))} {currency}
                    </Typography>
                  ))}
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>{tt('Net Kullandırım', 'Net Disbursement')}:</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">{fmt(netDisbursed)} {currency}</Typography>
                <Typography variant="body2" color="textSecondary">{tt('Taksit Sayısı', 'Installment Count')}:</Typography>
                <Typography variant="body2">{installments.length}</Typography>
                <Typography variant="body2" color="textSecondary">{tt('İlk / Son Vade', 'First / Last Due')}:</Typography>
                <Typography variant="body2">
                  {installments.length > 0 ? `${installments[0].dueDate} / ${installments[installments.length - 1].dueDate}` : '-'}
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{tt('İptal', 'Cancel')}</Button>
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep((s) => s - 1)} disabled={loading}>
            {tt('Geri', 'Back')}
          </Button>
        )}
        {activeStep < 2 && (
          <Button
            variant="contained"
            onClick={() => setActiveStep((s) => s + 1)}
            disabled={loading || (activeStep === 0 ? !step1Valid : !step2Valid)}
          >
            {tt('İleri', 'Next')}
          </Button>
        )}
        {activeStep === 2 && (
          <Button
            variant="contained"
            color="success"
            onClick={handleSave}
            disabled={loading || !step1Valid || !step2Valid || !step3Valid}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {tt('Kaydet', 'Save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddLoanModal;
